export type ButtonSampleKind = 'label' | 'icon';

export interface ButtonSample {
  id: string;
  label: string;
  classes: string;
  kind: ButtonSampleKind;
  state: 'enabled' | 'disabled';
}

export interface ButtonGroup {
  id: string;
  title: string;
  description: string;
  samples: readonly ButtonSample[];
}

export const BUTTON_CATALOG: readonly ButtonGroup[] = [
  {
    id: 'shout',
    title: 'Shout',
    description: 'Main SHOUT buttons. Pair `btn-shout` with a size and a colour class. FAB variants are standalone.',
    samples: [
      { id: 'btn-shout-xs', label: 'Button', classes: 'btn-shout btn-shout-xs btn-shout-primary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-sm', label: 'Button', classes: 'btn-shout btn-shout-sm btn-shout-primary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-md', label: 'Button', classes: 'btn-shout btn-shout-md btn-shout-primary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-fab-primary', label: 'Add', classes: 'btn-shout-fab-primary', kind: 'icon', state: 'enabled' },
      { id: 'btn-shout-xs-secondary', label: 'Button', classes: 'btn-shout btn-shout-xs btn-shout-secondary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-sm-secondary', label: 'Button', classes: 'btn-shout btn-shout-sm btn-shout-secondary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-md-secondary', label: 'Button', classes: 'btn-shout btn-shout-md btn-shout-secondary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-fab-secondary', label: 'Add', classes: 'btn-shout-fab-secondary', kind: 'icon', state: 'enabled' },

      { id: 'btn-shout-xs-tertiary', label: 'Button', classes: 'btn-shout btn-shout-xs btn-shout-tertiary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-sm-tertiary', label: 'Button', classes: 'btn-shout btn-shout-sm btn-shout-tertiary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-md-tertiary', label: 'Button', classes: 'btn-shout btn-shout-md btn-shout-tertiary', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-fab-tertiary', label: 'Add', classes: 'btn-shout-fab-tertiary', kind: 'icon', state: 'enabled' },

      { id: 'btn-shout-xs-alert', label: 'Button', classes: 'btn-shout btn-shout-xs btn-shout-alert', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-sm-alert', label: 'Button', classes: 'btn-shout btn-shout-sm btn-shout-alert', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-md-alert', label: 'Button', classes: 'btn-shout btn-shout-md btn-shout-alert', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-fab-alert', label: 'Add', classes: 'btn-shout-fab-alert', kind: 'icon', state: 'enabled' },
      { id: 'btn-shout-xs-plus-gradient', label: 'Button', classes: 'btn-shout btn-shout-xs btn-shout-plus-gradient', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-sm-plus-gradient', label: 'Button', classes: 'btn-shout btn-shout-sm btn-shout-plus-gradient', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-md-plus-gradient', label: 'Button', classes: 'btn-shout btn-shout-md btn-shout-plus-gradient', kind: 'label', state: 'enabled' },
      { id: 'btn-shout-plus-fab', label: 'Add', classes: 'btn-shout-plus-fab', kind: 'icon', state: 'enabled' },
    ],
  },
  {
    id: 'primary',
    title: 'Primary',
    description: 'Filled actions — default, gradient, success, danger, and colour variants.',
    samples: [
      { id: 'primary-button', label: 'Button', classes: 'primary-button', kind: 'label', state: 'enabled' },
      { id: 'primary-button-full', label: 'Button', classes: 'primary-button-full', kind: 'label', state: 'enabled' },
      { id: 'btn-shout btn-shout-xs btn-shout-primary', label: 'Button', classes: 'btn-shout btn-shout-xs btn-shout-primary', kind: 'label', state: 'enabled' },
      { id: 'primary-go-button', label: 'Button', classes: 'primary-go-button', kind: 'label', state: 'enabled' },
      { id: 'flashy-go-button', label: 'Button', classes: 'flashy-go-button', kind: 'label', state: 'enabled' },
      { id: 'auto-pilot-button', label: 'Button', classes: 'auto-pilot-button', kind: 'label', state: 'enabled' },
      { id: 'primary-button-fuchsia', label: 'Button', classes: 'primary-button-fuchsia', kind: 'label', state: 'enabled' },
      { id: 'highlight-button-small', label: 'Button', classes: 'highlight-button-small', kind: 'label', state: 'enabled' },
      { id: 'primary-button-success', label: 'Button', classes: 'primary-button-success', kind: 'label', state: 'enabled' },
      {
        id: 'primary-button-success-full',
        label: 'Button',
        classes: 'primary-button-success-full',
        kind: 'label',
        state: 'enabled'
      },
      { id: 'primary-button-danger', label: 'Button', classes: 'primary-button-danger', kind: 'label', state: 'enabled' },
      { id: 'primary-lime', label: 'Button', classes: 'primary-lime', kind: 'label', state: 'enabled' },
      { id: 'primary-teal', label: 'Button', classes: 'primary-teal', kind: 'label', state: 'enabled' },
      { id: 'primary-teal-full', label: 'Button', classes: 'primary-teal-full', kind: 'label', state: 'enabled' },
    ],
  },
  {
    id: 'secondary',
    title: 'Secondary',
    description: 'Outlined and muted actions, including full-width and compact sizes.',
    samples: [
      { id: 'secondary-button', label: 'Button', classes: 'secondary-button', kind: 'label', state: 'enabled' },
      { id: 'secondary-button-full', label: 'Button', classes: 'secondary-button-full', kind: 'label', state: 'enabled' },
      {
        id: 'secondary-button-full-big',
        label: 'Button',
        classes: 'secondary-button-full-big',
        kind: 'label',
        state: 'enabled'
      },
      {
        id: 'secondary-button-full-small',
        label: 'Button',
        classes: 'secondary-button-full-small',
        kind: 'label',
        state: 'enabled'
      },
      { id: 'secondary-button-small', label: 'Button', classes: 'secondary-button-small', kind: 'label', state: 'enabled' },
      { id: 'tertiary-button', label: 'Button', classes: 'tertiary-button', kind: 'label', state: 'enabled' },
    ],
  },
  {
    id: 'basic',
    title: 'Basic',
    description: 'Size class plus a colour class — used together on SHOUT+ and admin screens.',
    samples: [
      { id: 'btn-basic-primary', label: 'Button', classes: 'btn-basic btn-primary', kind: 'label', state: 'enabled' },
      {
        id: 'btn-basic-primary-outline',
        label: 'Button',
        classes: 'btn-basic btn-primary-outline',
        kind: 'label',
        state: 'enabled'
      },
      { id: 'btn-basic-secondary', label: 'Button', classes: 'btn-basic btn-secondary', kind: 'label', state: 'enabled' },
      {
        id: 'btn-basic-secondary-outline',
        label: 'Button',
        classes: 'btn-basic btn-secondary-outline',
        kind: 'label',
        state: 'enabled'
      },
      {
        id: 'btn-basic-gray-outline',
        label: 'Button',
        classes: 'btn-basic btn-gray-outline',
        kind: 'label',
        state: 'enabled'
      },
      { id: 'btn-basic-sm-primary', label: 'Button', classes: 'btn-basic-sm btn-primary', kind: 'label', state: 'enabled' },
      {
        id: 'btn-basic-sm-primary-outline',
        label: 'Button',
        classes: 'btn-basic-sm btn-primary-outline',
        kind: 'label',
        state: 'enabled'
          },
      {
        id: 'btn-basic-sm-secondary',
        label: 'Button',
        classes: 'btn-basic-sm btn-secondary',
        kind: 'label',
        state: 'enabled'
      },
      {
        id: 'btn-basic-sm-secondary-outline',
        label: 'Button',
        classes: 'btn-basic-sm btn-secondary-outline',
        kind: 'label',
        state: 'enabled'
      },
      {
        id: 'btn-basic-sm-gray-outline',
        label: 'Button',
        classes: 'btn-basic-sm btn-gray-outline',
        kind: 'label',
        state: 'enabled'
      },
    ],
  },
  {
    id: 'fab',
    title: 'FAB',
    description: 'Round icon buttons. Pair `btn-fab` with a size and a colour class.',
    samples: [
      { id: 'fab-button-primary', label: 'Add', classes: 'fab-button-primary', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-secondary', label: 'Add', classes: 'fab-button-secondary', kind: 'icon', state: 'enabled' },
      {
        id: 'fab-button-secondary-small',
        label: 'Add',
        classes: 'fab-button-secondary-small',
        kind: 'icon',
        state: 'enabled'
      },
      { id: 'fab-button-back', label: 'Add', classes: 'fab-button-back', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-flashy-go', label: 'Add', classes: 'fab-button-flashy-go', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-highlight', label: 'Add', classes: 'fab-button-highlight', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-subtle', label: 'Add', classes: 'fab-button-subtle', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-custom', label: 'Add', classes: 'fab-button-custom', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-delete', label: 'Add', classes: 'fab-button-delete', kind: 'icon', state: 'enabled' },
      { id: 'fab-button-confirm', label: 'Add', classes: 'fab-button-confirm', kind: 'icon', state: 'enabled' },
      {
        id: 'fab-button-shout-plus-gradient',
        label: 'Add',
        classes: 'fab-button-shout-plus-gradient',
        kind: 'icon',
        state: 'enabled'
            },
      {
        id: 'fab-button-shout-plus-gradient-light',
        label: 'Add',
        classes: 'fab-button-shout-plus-gradient-light',
        kind: 'icon',
        state: 'enabled'
                    },
      { id: 'btn-fab-xs', label: 'Add', classes: 'btn-fab btn-fab-xs btn-primary', kind: 'icon', state: 'enabled' },
      { id: 'btn-fab-sm', label: 'Add', classes: 'btn-fab btn-fab-sm btn-primary', kind: 'icon', state: 'enabled' },
      { id: 'btn-fab-md', label: 'Add', classes: 'btn-fab btn-fab-md btn-primary', kind: 'icon', state: 'enabled' },
      { id: 'btn-fab-lg', label: 'Add', classes: 'btn-fab btn-fab-lg btn-primary', kind: 'icon', state: 'enabled' },
      {
        id: 'btn-fab-sm-secondary',
        label: 'Add',
        classes: 'btn-fab btn-fab-sm btn-secondary',
        kind: 'icon',
        state: 'enabled'
        },
      {
        id: 'btn-fab-sm-primary-outline',
        label: 'Add',
        classes: 'btn-fab btn-fab-sm btn-primary-outline',
        kind: 'icon',
        state: 'enabled'
            },
    ],
  },
  {
    id: 'nav',
    title: 'Navigation',
    description: 'Tab-style buttons used in local navigation.',
    samples: [
      { id: 'nav-tab', label: 'Tab', classes: 'nav-tab', kind: 'label', state: 'enabled' },
      { id: 'nav-tab-active', label: 'Tab', classes: 'nav-tab nav-tab-active', kind: 'label', state: 'enabled' },
    ],
  },
];
